import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChevronLeft, ChevronRight, Calendar, Clock, Plane, Building2 } from 'lucide-react';
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths,
  isToday, startOfWeek, endOfWeek, parseISO, isWithinInterval,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { OrdenTrabajoVistaDetalle } from './OrdenTrabajoVistaDetalle';
import { useIsMobile } from '@/hooks/use-mobile';

import { OrdenTrabajo } from '@/contexts/OrdenesTrabajoContext';
import { ObraJornada } from '@/contexts/ObrasContext';
import { Ausencia, TIPO_AUSENCIA_LABEL, TIPO_AUSENCIA_COLOR } from '@/contexts/AusenciasContext';

interface MiCalendarioMesProps {
  ordenes: OrdenTrabajo[];
  jornadas?: ObraJornada[];
  ausencias?: Ausencia[];
}

const prioridadBg: Record<string, string> = {
  urgente: 'bg-red-500/20 border-red-500/50 text-red-700 dark:text-red-300',
  alta: 'bg-orange-500/20 border-orange-500/50 text-orange-700 dark:text-orange-300',
  media: 'bg-amber-500/20 border-amber-500/50 text-amber-700 dark:text-amber-300',
  baja: 'bg-emerald-500/20 border-emerald-500/50 text-emerald-700 dark:text-emerald-300',
};

const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

export const MiCalendarioMes: React.FC<MiCalendarioMesProps> = ({
  ordenes, jornadas = [], ausencias = [],
}) => {
  const isMobile = useIsMobile();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedOrden, setSelectedOrden] = useState<OrdenTrabajo | null>(null);

  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  const dataByDate = useMemo(() => {
    const map: Record<string, { ordenes: OrdenTrabajo[]; jornadas: ObraJornada[]; ausencias: Ausencia[] }> = {};
    calendarDays.forEach(day => {
      const key = format(day, 'yyyy-MM-dd');
      map[key] = { ordenes: [], jornadas: [], ausencias: [] };
    });
    ordenes.filter(o => o.estado !== 'cancelada').forEach(o => {
      if (!o.fecha_limite) return;
      const k = format(new Date(o.fecha_limite), 'yyyy-MM-dd');
      if (map[k]) map[k].ordenes.push(o);
    });
    jornadas.forEach(j => {
      if (map[j.fecha]) map[j.fecha].jornadas.push(j);
    });
    ausencias.filter(a => a.estado_validacion !== 'rechazada').forEach(a => {
      try {
        const start = parseISO(a.fecha_inicio);
        const end = parseISO(a.fecha_fin);
        calendarDays.forEach(day => {
          if (isWithinInterval(day, { start, end })) {
            const k = format(day, 'yyyy-MM-dd');
            map[k].ausencias.push(a);
          }
        });
      } catch { /* ignore */ }
    });
    return map;
  }, [ordenes, jornadas, ausencias, calendarDays]);

  const isCurrentMonth = (d: Date) => d.getMonth() === currentMonth.getMonth();

  return (
    <div className="space-y-4">
      {/* Navigation */}
      <div className="flex items-center justify-between bg-card/80 backdrop-blur-sm rounded-lg p-3 border">
        <Button variant="outline" size="sm" onClick={() => setCurrentMonth(p => subMonths(p, 1))} className="gap-1">
          <ChevronLeft className="h-4 w-4" /> Anterior
        </Button>
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold capitalize">
            {format(currentMonth, 'MMMM yyyy', { locale: es })}
          </h2>
        </div>
        <Button variant="outline" size="sm" onClick={() => setCurrentMonth(p => addMonths(p, 1))} className="gap-1">
          Siguiente <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-xs bg-card/50 rounded-lg p-2 border">
        <span className="font-medium text-muted-foreground">Leyenda:</span>
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-red-500" /><span>OT urgente</span></div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-cyan-500" /><span>Jornada</span></div>
        <div className="flex items-center gap-1"><Plane className="h-3 w-3" /><span>Ausencia</span></div>
      </div>

      {/* Calendar */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="grid grid-cols-7 bg-muted/50 border-b">
            {WEEKDAYS.map(d => (
              <div key={d} className="p-2 text-center text-xs sm:text-sm font-medium text-muted-foreground">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {calendarDays.map(day => {
              const key = format(day, 'yyyy-MM-dd');
              const d = dataByDate[key];
              const inMonth = isCurrentMonth(day);
              const today = isToday(day);
              const total = d.ordenes.length + d.jornadas.length + d.ausencias.length;
              return (
                <div
                  key={key}
                  className={cn(
                    "min-h-[90px] sm:min-h-[120px] border-b border-r p-1 transition-colors",
                    !inMonth && "bg-muted/30",
                    today && "bg-primary/5"
                  )}
                >
                  <div className={cn("flex items-center justify-between p-1", !inMonth && "opacity-40")}>
                    <span className={cn(
                      "text-xs sm:text-sm font-medium rounded-full w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center",
                      today && "bg-primary text-primary-foreground"
                    )}>
                      {format(day, 'd')}
                    </span>
                    {total > 0 && <Badge variant="secondary" className="text-[10px] h-5 px-1.5">{total}</Badge>}
                  </div>
                  <ScrollArea className={cn(isMobile ? "h-[50px]" : "h-[80px]")}>
                    <div className="space-y-1">
                      {d.ausencias.map(a => {
                        const aprobada = a.estado_validacion === 'aprobada';
                        return (
                          <div
                            key={`a-${a.id}`}
                            className={cn(
                              "px-1 py-0.5 rounded border-l-2 text-[9px] sm:text-[10px] truncate flex items-center gap-1",
                              aprobada ? "border-l-green-500 bg-green-500/10" : "border-l-amber-500 bg-amber-500/10"
                            )}
                            title={`${TIPO_AUSENCIA_LABEL[a.tipo]} · ${aprobada ? 'Aprobada' : 'Pendiente'}`}
                          >
                            <Plane className="h-2.5 w-2.5 shrink-0" />
                            <span className="truncate font-semibold">{TIPO_AUSENCIA_LABEL[a.tipo]}</span>
                          </div>
                        );
                      })}
                      {d.ordenes.map(o => (
                        <div
                          key={o.id}
                          onClick={() => setSelectedOrden(o)}
                          className={cn(
                            "px-1 py-0.5 rounded border text-[9px] sm:text-[10px] cursor-pointer truncate",
                            prioridadBg[o.prioridad]
                          )}
                          title={`${o.numero_ot} · ${o.titulo}`}
                        >
                          <span className="font-semibold">{o.hora_ejecucion?.slice(0, 5) || ''}</span>{' '}
                          <span className="truncate">{o.titulo}</span>
                        </div>
                      ))}
                      {d.jornadas.map(j => {
                        const horas = j.horas_trabajadas ? Number(j.horas_trabajadas).toFixed(1) : null;
                        const titulo = j.obra?.nombre || j.orden_trabajo?.titulo || j.cliente?.nombre || 'Jornada';
                        return (
                          <div
                            key={`j-${j.id}`}
                            className="px-1 py-0.5 rounded border bg-cyan-500/15 border-cyan-500/40 text-cyan-700 dark:text-cyan-300 text-[9px] sm:text-[10px] truncate flex items-center gap-1"
                            title={titulo}
                          >
                            <Clock className="h-2.5 w-2.5 shrink-0" />
                            {horas && <span className="font-semibold">{horas}h</span>}
                            <span className="truncate">{titulo}</span>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {selectedOrden && (
        <OrdenTrabajoVistaDetalle
          orden={selectedOrden}
          isOpen={!!selectedOrden}
          onClose={() => setSelectedOrden(null)}
        />
      )}
    </div>
  );
};
